import React, { useEffect, useRef, useState } from 'react';
import { HexColorPicker, HexColorInput } from 'react-colorful';

interface ColorSwatchPickerProps {
  color: string;
  onChange: (hex: string) => void;
  fallback?: string;
  compact?: boolean;
}

function isLightColor(hex: string): boolean {
  const clean = (hex || '').replace('#', '');
  if (clean.length !== 6) return true;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
}

const ColorSwatchPicker: React.FC<ColorSwatchPickerProps> = ({ color, onChange, fallback = '#f97316', compact = false }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const safeColor = /^#[0-9a-fA-F]{6}$/.test(color) ? color : fallback;
  const textColor = isLightColor(safeColor) ? '#1f2937' : '#ffffff';

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className={compact ? 'relative inline-block' : 'relative w-full'} ref={wrapRef}>
      <style>{`
        .sp-capsule-picker.react-colorful{width:100%;height:110px;border-radius:12px;overflow:hidden}
        .sp-capsule-picker .react-colorful__saturation{border-radius:12px 12px 0 0}
        .sp-capsule-picker .react-colorful__hue{height:14px;border-radius:0 0 12px 12px}
        .sp-capsule-picker .react-colorful__pointer{width:14px;height:14px;border-width:2px}
      `}</style>
      {compact ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-8 h-8 rounded-full border-2 border-white shadow-sm ring-1 ring-black/10 transition-transform hover:scale-110"
          style={{ backgroundColor: safeColor }}
          title={safeColor.toUpperCase()}
        />
      ) : (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full h-10 rounded-full flex items-center justify-center gap-2 font-mono text-sm font-semibold shadow-sm border border-black/5 transition-transform hover:scale-[1.02]"
          style={{ backgroundColor: safeColor, color: textColor }}
        >
          {safeColor.toUpperCase()}
        </button>
      )}

      {open && (
        <div
          className={`absolute z-50 mt-2 bg-white rounded-2xl shadow-xl p-3 border border-gray-100 ${compact ? 'left-1/2 -translate-x-1/2' : 'left-0'}`}
          style={{ width: 200 }}
          onClick={(e) => e.stopPropagation()}
        >
          <HexColorPicker className="sp-capsule-picker" color={safeColor} onChange={onChange} />
          <div className="flex items-center gap-2 mt-3">
            <span className="w-7 h-7 rounded-full border border-gray-200 flex-shrink-0" style={{ backgroundColor: safeColor }} />
            <div className="flex-1 flex items-center gap-1 border border-gray-200 rounded-lg px-2 py-1">
              <span className="text-xs text-gray-400">#</span>
              <HexColorInput className="w-full text-sm outline-none" color={safeColor} onChange={onChange} prefixed={false} />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="w-full mt-3 py-2 rounded-full text-white text-sm font-semibold"
            style={{ background: 'linear-gradient(120deg, #00B4A1, #3A93B9)' }}
          >
            เสร็จสิ้น
          </button>
        </div>
      )}
    </div>
  );
};

export default ColorSwatchPicker;
